import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { PERMISSIONS } from "../utils/permissions";

interface ReturnNotification {
  id: string;
  orderId: string;
  orderNumber?: string;
  type: "return" | "exchange";
  status: string;
  createdAt: string;
}

interface ReturnsNotificationsContextType {
  pendingReturnsCount: number;
}

const ReturnsNotificationsContext = createContext<
  ReturnsNotificationsContextType | undefined
>(undefined);

const getStoredToken = () =>
  localStorage.getItem("authToken") || localStorage.getItem("token");

const isJwtExpired = (token: string) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return false;
    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = `${normalizedPayload}${"=".repeat(
      (4 - (normalizedPayload.length % 4)) % 4,
    )}`;
    const decodedPayload = JSON.parse(atob(paddedPayload));
    return (
      typeof decodedPayload.exp === "number" &&
      decodedPayload.exp * 1000 <= Date.now()
    );
  } catch {
    return false;
  }
};

const getStreamUrl = (token: string) => {
  const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL?.replace(
    /\/$/,
    "",
  );
  if (!configuredBaseUrl) return null;

  const apiBaseUrl = configuredBaseUrl.endsWith("/api")
    ? configuredBaseUrl
    : `${configuredBaseUrl}/api`;

  return `${apiBaseUrl}/returns/notifications/stream?token=${encodeURIComponent(
    token,
  )}`;
};

const getCount = (value: unknown) => {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null;
};

export const ReturnsNotificationsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { isAuthenticated, isLoading, token, user, hasPermission } = useAuth();
  const { info } = useToast();
  const [pendingReturnsCount, setPendingReturnsCount] = useState(0);
  const seenRequestIds = useRef(new Set<string>());

  const canReadReturns =
    isAuthenticated &&
    user?.role !== "customer" &&
    hasPermission(PERMISSIONS.VIEW_RETURNS);

  useEffect(() => {
    if (
      isLoading ||
      !canReadReturns ||
      typeof window === "undefined" ||
      !("EventSource" in window)
    ) {
      setPendingReturnsCount(0);
      return;
    }

    let eventSource: EventSource | null = null;
    let reconnectTimer: number | undefined;
    let reconnectAttempts = 0;
    let stopped = false;

    const handleCount = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data);
        const count = getCount(payload.count);
        if (count !== null) setPendingReturnsCount(count);
      } catch {
        // Keep the previous count if a malformed event arrives.
      }
    };

    const handleNewReturnRequest = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data) as {
          count?: unknown;
          returnRequest?: ReturnNotification;
        };
        const count = getCount(payload.count);
        if (count !== null) setPendingReturnsCount(count);

        const request = payload.returnRequest;
        if (!request?.id || seenRequestIds.current.has(request.id)) return;

        seenRequestIds.current.add(request.id);
        if (seenRequestIds.current.size > 100) {
          const firstRequestId = seenRequestIds.current.values().next().value;
          if (firstRequestId) seenRequestIds.current.delete(firstRequestId);
        }

        info(
          `New ${request.type || "return"} request for order ${
            request.orderNumber || request.orderId
          }`,
        );
      } catch {
        // Ignore malformed stream events and keep the connection active.
      }
    };

    const closeStream = () => {
      if (!eventSource) return;
      eventSource.removeEventListener("count", handleCount);
      eventSource.removeEventListener(
        "new_return_request",
        handleNewReturnRequest,
      );
      eventSource.onopen = null;
      eventSource.onerror = null;
      eventSource.close();
      eventSource = null;
    };

    const clearReconnectTimer = () => {
      if (reconnectTimer !== undefined) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = undefined;
      }
    };

    const connect = () => {
      clearReconnectTimer();
      if (stopped) return;

      const currentToken = getStoredToken();
      if (!currentToken || isJwtExpired(currentToken)) return;

      const streamUrl = getStreamUrl(currentToken);
      if (!streamUrl) return;

      closeStream();
      const stream = new EventSource(streamUrl);
      eventSource = stream;

      stream.addEventListener("count", handleCount);
      stream.addEventListener("new_return_request", handleNewReturnRequest);
      stream.onopen = () => {
        reconnectAttempts = 0;
      };
      stream.onerror = () => {
        if (stopped || eventSource !== stream) return;
        closeStream();

        const freshToken = getStoredToken();
        if (!freshToken || isJwtExpired(freshToken)) return;

        const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000);
        reconnectAttempts += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };
    };

    const reconnectWithFreshToken = () => {
      reconnectAttempts = 0;
      closeStream();
      connect();
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "authToken" || event.key === "token") {
        reconnectWithFreshToken();
      }
    };

    window.addEventListener("authChanged", reconnectWithFreshToken);
    window.addEventListener("storage", handleStorageChange);
    connect();

    return () => {
      stopped = true;
      clearReconnectTimer();
      closeStream();
      window.removeEventListener("authChanged", reconnectWithFreshToken);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [canReadReturns, info, isLoading, token]);

  const value = useMemo(
    () => ({ pendingReturnsCount }),
    [pendingReturnsCount],
  );

  return (
    <ReturnsNotificationsContext.Provider value={value}>
      {children}
    </ReturnsNotificationsContext.Provider>
  );
};

export const useReturnsNotifications = () => {
  const context = useContext(ReturnsNotificationsContext);
  if (!context) {
    throw new Error(
      "useReturnsNotifications must be used within ReturnsNotificationsProvider",
    );
  }
  return context;
};
